const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '';

export interface VineyardTag {
  key: string;
  label: string;
}

export interface FeaturedVineyard {
  id: string;
  name: string;
  description: string;
  region: string;
  regionColor: string;
  averageRating: number | null;
  isFeatured: boolean;
  isSuggested: boolean;
  isTrending: boolean;
  hasAccommodation: boolean;
  coverImageUrl: string;
  coverImageUpdatedAt: string;
  latitude: number;
  longitude: number;
  tags: VineyardTag[];
  transport: unknown[];
}

// Raw API response types
interface RawVineyard {
  id: string;
  name: string;
  description: string;
  region: string;
  regionColour: string; // Note: British spelling in API
  averageRating: number | null;
  isFeatured: boolean;
  isSuggested: boolean;
  isTrending: boolean;
  hasAccommodation: boolean;
  coverImageUrl: string;
  coverImageUpdatedAt: string;
  latitude: number;
  longitude: number;
  tags: VineyardTag[];
  transport: unknown[];
}

interface FeaturedVineyardsResponse {
  counts: {
    suggested: number;
    trending: number;
    featured: number;
    withAccommodation: number;
    withTransportation: number;
  };
  vineyards: RawVineyard[];
}

async function getJson<T>(path: string): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });

  if (!response.ok) {
    throw new Error(`Request failed with status ${response.status}`);
  }

  const contentType = response.headers.get('content-type');
  if (!contentType?.includes('application/json')) {
    const text = await response.text();
    throw new Error(
      `Expected JSON response but received ${contentType || 'unknown'}: ${text.substring(0, 100)}`
    );
  }

  return (await response.json()) as T;
}

export async function fetchFeaturedVineyards(): Promise<FeaturedVineyard[]> {
  const response = await getJson<FeaturedVineyardsResponse>(
    '/vineyards?featuredVineyards=true'
  );
  // Transform raw API response to internal format (regionColour -> regionColor)
  return response.vineyards.map((vineyard) => ({
    ...vineyard,
    regionColor: vineyard.regionColour,
  })) as FeaturedVineyard[];
}
